import { useRef, useCallback } from 'react';
import { gsap } from 'gsap';

interface SparkleParticle {
  x: number;
  y: number;
  size: number;
  element: HTMLDivElement;
}

interface SparkleRect {
  width: number;
  height: number;
  left: number;
  top: number;
}

export const useSparklingEffect = () => {
  const containerRef = useRef<HTMLElement>(null);
  const particlesRef = useRef<SparkleParticle[]>([]);
  const animationRef = useRef<gsap.core.Timeline | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const createParticle = useCallback((x: number, y: number): SparkleParticle => {
    const particle = document.createElement('div');
    particle.className = 'sparkle-particle';
    
    const size = Math.random() * 6 + 2; // Random size between 2-8px
    particle.style.cssText = `
      position: absolute;
      width: ${size}px;
      height: ${size}px;
      background: var(--accent-color);
      pointer-events: none;
      z-index: 1000;
      left: ${x}px;
      top: ${y}px;
      clip-path: polygon(50% 0%, 60% 40%, 100% 50%, 60% 60%, 50% 100%, 40% 60%, 0% 50%, 40% 40%);
      filter: drop-shadow(0 0 ${size}px var(--accent-color));
    `;
    
    return {
      x,
      y,
      size,
      element: particle
    };
  }, []);

  const createSparkles = useCallback((rect: SparkleRect, count: number = 6) => {
    if (!containerRef.current) return [];
    
    const sparkles: SparkleParticle[] = [];
    
    for (let i = 0; i < count; i++) {
      const x = Math.random() * rect.width;
      const y = Math.random() * rect.height;
      const particle = createParticle(x, y);
      
      containerRef.current.appendChild(particle.element);
      sparkles.push(particle);
    }
    
    return sparkles;
  }, [createParticle]);

  const animateSparkles = useCallback((sparkles: SparkleParticle[]) => {
    sparkles.forEach((sparkle, index) => {
      const delay = index * 0.05;
      const duration = 1.2 + Math.random() * 0.8;
      
      // Random movement direction
      const angle = Math.random() * Math.PI * 2;
      const distance = 15 + Math.random() * 25;
      const endX = sparkle.x + Math.cos(angle) * distance;
      const endY = sparkle.y + Math.sin(angle) * distance;
      
      gsap.timeline()
        .fromTo(sparkle.element, 
          {
            scale: 0,
            opacity: 0,
            rotation: 0
          },
          {
            scale: 1,
            opacity: 1,
            rotation: 360,
            duration: duration * 0.2,
            ease: "back.out(1.7)",
            delay
          })
        .to(sparkle.element, {
          x: endX - sparkle.x,
          y: endY - sparkle.y,
          opacity: 0,
          scale: 0.2,
          duration: duration * 0.8,
          ease: "power2.out",
          onComplete: () => {
            if (sparkle.element.parentNode) {
              sparkle.element.parentNode.removeChild(sparkle.element);
            }
          }
        }, duration * 0.1);
    });
  }, []);

  const startSparklingEffect = useCallback((element: HTMLElement) => {
    // Clear any existing effect
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    if (animationRef.current) {
      animationRef.current.kill();
    }
    
    containerRef.current = element;
    const rect = element.getBoundingClientRect();
    const parentRect = element.offsetParent?.getBoundingClientRect() || { left: 0, top: 0 };
    
    // Adjust coordinates relative to the parent container
    const adjustedRect = {
      width: rect.width,
      height: rect.height,
      left: rect.left - parentRect.left,
      top: rect.top - parentRect.top
    };
    
    // Create initial sparkles
    const initialSparkles = createSparkles(adjustedRect);
    animateSparkles(initialSparkles);
    
    // Continuously create new sparkles while hovering
    intervalRef.current = setInterval(() => {
      if (containerRef.current) {
        const newSparkles = createSparkles(adjustedRect);
        animateSparkles(newSparkles);
      }
    }, 200); // Create new sparkles every 200ms
  }, [createSparkles, animateSparkles]);

  const stopSparklingEffect = useCallback(() => {
    // Clear the interval to stop creating new particles
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    if (animationRef.current) {
      animationRef.current.kill();
      animationRef.current = null;
    }
    
    // Clean up any remaining particles
    particlesRef.current.forEach(particle => {
      if (particle.element.parentNode) {
        particle.element.parentNode.removeChild(particle.element);
      }
    });
    
    particlesRef.current = [];
  }, []);

  return {
    startSparklingEffect,
    stopSparklingEffect
  };
};
